<template>
  <router-view v-if="dataLoaded" />
</template>

<script lang="ts">
import {
  nextTick,
  computed,
  defineComponent,
  onMounted,
  getCurrentInstance
} from 'vue';
import { RouterView } from 'vue-router';
import compileData from './lib/compileData';
import SCORM, { debouncedSaveSuspendData, getCompleted } from './lib/SCORM';
import gsap from 'gsap';
import { getViewed } from './lib/SCORM';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ref, reactive, provide } from 'vue';
import { suspendInjectionKey } from './lib/useSuspend';
import getQueryParameter from './lib/getQueryParameter';
import { initTextInflation } from './lib/textInflation';
import useEditMode from './stores/useEditMode';
import { storeToRefs } from 'pinia';
import useContent from './stores/useContent';
import isHidden from './lib/isHidden';

gsap.registerPlugin(ScrollToPlugin);

export default defineComponent({
  components: { RouterView },
  provide() {
    return {
      audioDoc: Boolean(getQueryParameter('audioDoc')),
      wordDoc: computed(
        () =>
          Boolean(this.suspend?.tags?.worddoc) ||
          Boolean(getQueryParameter('word'))
      ),
      highlightAnswers: computed(() =>
        Boolean(this.suspend?.tags?.highlightanswers)
      ),
      thumbnailType: getQueryParameter('thumbnail'),
      bookmark: computed(() => this.bookmark),
      goToBookmark: computed(() => this.goToBookmark),
      goToSection: computed(() => this.goToSection),
      currentLocation: computed(() => this.currentLocation)
    };
  },
  setup() {
    const { compiled } = useContent();
    const { suspend: defaultSuspend } = compileData(compiled);
    const dataLoaded = ref(false);
    const bookmark = compiled?.pages?.some(p => p.id === SCORM.getLocation())
      ? SCORM.getLocation()
      : null;
    const data = reactive({ bookmark });

    // some bookmark logging
    console.log(`Checking SCORM bookmark: ${SCORM.getLocation()}`);
    if (bookmark) console.log(`Found bookmarked page: ${bookmark}`);
    if (compiled.hidden?.[bookmark])
      console.log(`Bookmarked page ${bookmark} is hidden`);

    const clone = (o: object) => JSON.parse(JSON.stringify(o));

    const suspend = reactive<Suspend>(clone(defaultSuspend));
    const { available: isEditModeAvailable, mode } = storeToRefs(useEditMode());
    const isEditModeActive = computed(() => mode.value === 'edit');

    provide('suspend', suspend);
    provide(suspendInjectionKey, suspend);
    provide('data', data);
    provide('isEditModeAvailable', isEditModeAvailable);
    provide('isEditModeActive', isEditModeActive);

    initTextInflation();
    window.app = { suspend };

    onMounted(async () => {
      await nextTick();
      dataLoaded.value = true;

      window.clearSuspendData = () => {
        Object.assign(suspend, clone(defaultSuspend));
      };
    });

    return {
      bookmark,
      suspend,
      dataLoaded,
      data,
      compiled
    };
  },
  computed: {
    units() {
      return this.content?.pages?.filter(p => p.type === 'unit');
    },
    currentLocation() {
      return this.$router.currentRoute.value?.params?.page;
    }
  },
  mounted() {
    // expose helper for AuthorPreview
    if (typeof window.goToSection !== 'function') {
      // bind so `this` still points at the root instance
      window.goToSection = this.goToSection.bind(this);
    }
  },
  watch: {
    $route(to, from) {
      // if we try to go to a hidden page send the router to the first page
      if (isHidden(to.params.page)) {
        this.$router.push(`/${this.compiled.pages[0].id}`);
        return;
      }

      if (to.params.page !== from.params.page) {
        document.body.dispatchEvent(
          new Event('uc:pagechange', {
            bubbles: true,
            data: { page: to.params.page }
          })
        );
      }
    },
    suspend: {
      deep: true,
      handler(s) {
        // console.log('LMSSetValue', JSON.stringify(s));
        // Skip SCORM saves in sales demo mode to prevent performance issues
        if (SCORM.isFirm('VINCI_ADMIN')) {
          return;
        }
        debouncedSaveSuspendData();
      }
    },
    'suspend.locale': {
      handler(v) {
        // console.log('suspend.locale', v);
      }
    }
  },
  methods: {
    async goToBookmark() {
      const { bookmark } = this.data;

      if (isHidden(bookmark)) return;

      if (this.currentLocation !== bookmark) {
        this.$router.push('/' + bookmark);

        await new Promise(r => setTimeout(r, 500));
      }

      // if we bookmarked a scrolling page, scroll to last viewed section
      const scroller = document.querySelector(`#${bookmark} .scroller`);

      if (scroller) {
        const sections = [
          ...document.querySelectorAll(`#${bookmark} .section`)
        ];
        const availableSections = sections.filter(s => !isHidden(s.id));
        const viewedSections = availableSections.filter(
          s => getViewed(s.id) || getCompleted(s.id)
        );
        const scrollTo = viewedSections.slice(-1)[0]; // the last viewed/completed section
        const sectionId = scrollTo?.id;

        // if there is only one viewed section and we are already on the bookmarked page, force scroll to the next section (like a continue button)
        if (viewedSections.length === 1 && this.currentLocation === bookmark) {
          const page = document.querySelector(`#${bookmark}`);
          page?.scrollOne('next', { force: true });
        } else {
          this.goToSection(bookmark, sectionId);
        }
      }
    },
    async goToSection(pageId, sectionId, { duration = 1 } = {}) {
      if (isHidden(pageId) || isHidden(sectionId)) return;

      if (this.currentLocation !== pageId) {
        this.$router.push('/' + pageId);

        await new Promise(r => setTimeout(r, 500));
      }

      const scroller = document.querySelector(`#${pageId} .scroller`);

      if (!scroller) return;

      if (!this.suspend.reducedMotion) {
        return new Promise(resolve => {
          gsap.to(scroller, {
            scrollTo: { y: `#${sectionId}` },
            duration,
            onComplete: resolve
          });
        });
      } else {
        const section = document.querySelector(`#${sectionId}`);

        if (section) {
          section.scrollIntoView({
            behavior: 'instant',
            block: 'start',
            inline: 'nearest'
          });
        }
      }
    }
  }
});
</script>
